import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  RangeCalendar,
  RangeCalendarStateContext,
  CalendarGrid,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  CalendarCell,
  DateValue,
} from 'react-aria-components';
import { today, getLocalTimeZone, DateFormatter, parseDate } from '@internationalized/date';
import { SearchFacet } from '../../../types/search';
import { useDateFilter } from '../contexts/PortalDateFilterProvider';

interface PortalDateRangeSelectorProps {
  readonly selectionType: 'weekly' | 'custom';
  readonly currentDateFacet: SearchFacet;
}

export default function PortalDateRangeSelector({ selectionType, currentDateFacet }: PortalDateRangeSelectorProps) {
  const defaultStart = getWeekRange(today('UTC'));
  const { selectedValue, setSelectedValue, setSelectedValueLabel } = useDateFilter(currentDateFacet.key);
  const [maxValue, setMaxValue] = useState<DateValue | null>(null);

  // Parse selectedValue to initialize selectedRange if it exists
  const initialRange = useMemo(() => {
    if (selectedValue) {
      try {
        const dates = selectedValue.split(',');
        if (dates.length >= 2) {
          return {
            start: parseDate(dates[0]),
            end: parseDate(dates[1]),
          };
        }
      } catch (e) {
        console.error('Error parsing selectedValue for initial range:', e);
      }
    }
    return defaultStart;
  }, [defaultStart, selectedValue]);

  const [selectedRange, setSelectedRange] = useState<{ start: DateValue; end: DateValue } | null>(initialRange);

  // Initialize selectedValue if empty
  useEffect(() => {
    if (selectedValue) return;
    const { start, end } = defaultStart;
    const value = formatValue(start, end);
    setSelectedValue(value);
    setSelectedValueLabel(`${formatDate(start)} - ${formatDate(end)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedRange when selectedValue changes from outside
  useEffect(() => {
    if (selectedValue) {
      try {
        const dates = selectedValue.split(',');
        if (dates.length >= 2) {
          const range = {
            start: parseDate(dates[0]),
            end: parseDate(dates[1]),
          };
          setSelectedRange(range);
        }
      } catch (e) {
        console.error('Error parsing selectedValue:', e);
      }
    }
  }, [selectedValue]);

  function getWeekRange(date: DateValue) {
    const jsDate = date.toDate('UTC');
    const dayOfWeek = jsDate.getDay() === 0 ? 7 : jsDate.getDay(); // Sunday -> 7
    const start = date.subtract({ days: dayOfWeek - 1 });
    const end = date.add({ days: 7 - dayOfWeek });
    return { start, end };
  }

  function stopDragSelection(e: React.MouseEvent) {
    if (selectionType !== 'weekly') return;
    e.stopPropagation();
  }

  function selectWholeWeek(date: DateValue) {
    if (selectionType !== 'weekly') return;
    const week = getWeekRange(date);
    setSelectedRange(week);
    const value = formatValue(week.start, week.end);
    setSelectedValue(value);
    setSelectedValueLabel(`${formatDate(week.start)} - ${formatDate(week.end)}`);
  }

  function handleChange(range: { start: DateValue; end: DateValue } | null) {
    if (selectionType !== 'custom') return;
    setSelectedRange(range);
    if (range) {
      const value = formatValue(range.start, range.end);
      setSelectedValue(value);
      setSelectedValueLabel(`${formatDate(range.start)} - ${formatDate(range.end)}`);
    }
  }
  function formatValue(start: DateValue, end: DateValue) {
    return `${start.toString()},${end.toString()}`;
  }

  function formatDate(date: DateValue) {
    const formatter = new DateFormatter('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    return formatter.format(date.toDate('UTC'));
  }

  return (
    <RangeCalendar
      value={selectedRange ?? undefined}
      onChange={handleChange}
      visibleDuration={{ months: 2 }}
      aria-label="Select date range"
      maxValue={maxValue}
    >
      <AnchorDateWatcher setMaxValue={setMaxValue} />
      <div className="portal-date-type-selector__section">
        <div className="portal-date-type-selector__nav">
          <Button slot="previous">
            <svg className="portal-date-type-selector__back-icon icon" aria-hidden="true">
              <use href="/dist/assets/icons/sprite.svg#arrow-left"></use>
            </svg>
          </Button>
          <CalendarMonthLabel offset={0} />
        </div>

        <CalendarGrid
          onPointerDownCapture={stopDragSelection}
          className="portal-date-type-selector__calendar-grid"
          weekdayStyle="short"
        >
          <CalendarGridHeader>
            {day => (
              <CalendarHeaderCell className="portal-date-type-selector__calendar-header-cell">{day}</CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody>
            {date => (
              <CalendarCell
                onClick={() => selectWholeWeek(date)}
                className="portal-date-type-selector__calendar-cell"
                date={date}
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>
      </div>
      <div className="portal-date-type-selector__section">
        <div className="portal-date-type-selector__nav">
          <CalendarMonthLabel offset={1} />
          <Button slot="next">
            <svg className="portal-date-type-selector__back-icon icon" aria-hidden="true">
              <use href="/dist/assets/icons/sprite.svg#arrow-right"></use>
            </svg>
          </Button>
        </div>
        <CalendarGrid
          onPointerDownCapture={stopDragSelection}
          className="portal-date-type-selector__calendar-grid"
          weekdayStyle="short"
          offset={{ months: 1 }}
        >
          <CalendarGridHeader>
            {day => (
              <CalendarHeaderCell className="portal-date-type-selector__calendar-header-cell">{day}</CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody>
            {date => (
              <CalendarCell
                onClick={() => selectWholeWeek(date)}
                className="portal-date-type-selector__calendar-cell"
                date={date}
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>
      </div>
    </RangeCalendar>
  );
}

function AnchorDateWatcher({ setMaxValue }: { setMaxValue: (date: DateValue) => void }): null {
  const state = useContext(RangeCalendarStateContext)!;
  useEffect(() => {
    if (state.anchorDate) {
      setMaxValue(state.anchorDate.add({ months: 3 }));
    }
  }, [state.anchorDate, setMaxValue]);
  return null;
}

function CalendarMonthLabel({ offset = 0 }) {
  const state = useContext(RangeCalendarStateContext)!;
  const startDate = state.visibleRange.start.add({ months: offset });
  const jsDate = startDate.toDate(getLocalTimeZone());
  const label = jsDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  return <span className="portal-date-type-selector__month-label">{label}</span>;
}
